import { useCallback } from 'react';

import {
	type AnalyticsEventPayload,
	UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';

import { useAnalyticsPropertiesContext } from './analytics-properties-context';
import { AI_MATE_ATTRIBUTES, type AIMateAttributeType } from './analytics-properties-context/types';
import { ANALYTICS_CHANNEL } from './constants';
import { getAttributesFromContexts } from './utils';

const isTownSquareEvent = () =>
	['home.atlassian.com', 'team.atlassian.com'].some((host) => window.location?.host.includes(host));

/**
 * A wrapper around the analytics-next hook to send analytics events to the ai mate channel
 */
export const useAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { propertiesRef } = useAnalyticsPropertiesContext();

	// TODO: Improve this functionality, potentially with a higher order provider for tags or similar
	// This is a temporary solution to add the 'townsquare' tag to operational events so Experience Tracker works in Atlas
	const maybeAtlasOperationalTags = useCallback(
		(inputPayload: AnalyticsEventPayload | UIAnalyticsEvent) => {
			const payload =
				inputPayload instanceof UIAnalyticsEvent ? inputPayload.payload : inputPayload;
			if (
				payload.actionSubject === 'ui' &&
				payload.eventType === 'operational' &&
				isTownSquareEvent()
			) {
				return ['townsquare'];
			}
			return [];
		},
		[],
	);

	const sendAnalyticsEvent = useCallback(
		(
			payload: AnalyticsEventPayload | UIAnalyticsEvent,
			options?: {
				removeDefaultTags?: boolean;
			},
		) => {
			const extraTags = options?.removeDefaultTags ? [] : ['atlaskit', 'aiMate'];
			const allTags = Array.from(
				new Set([
					...extraTags,
					...((payload as any).tags || []),
					...maybeAtlasOperationalTags(payload),
				]),
			);
			const { conversationChannelId, experienceId, product, agentId, fullScreen } =
				propertiesRef.current;

			const defaultAttributes: AIMateAttributeType = {
				[AI_MATE_ATTRIBUTES.agentId]: agentId,
				[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationChannelId,
				[AI_MATE_ATTRIBUTES.experienceId]: experienceId,
				[AI_MATE_ATTRIBUTES.product]: product,
				[AI_MATE_ATTRIBUTES.fullScreen]: fullScreen,
			};

			if (payload instanceof UIAnalyticsEvent) {
				const aiMateContextAttributes = getAttributesFromContexts(payload.context);

				payload
					.update({
						...payload.payload,
						tags: allTags,
						attributes: {
							...defaultAttributes,
							...payload.payload.attributes,
							...aiMateContextAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			} else {
				const event = createAnalyticsEvent({
					...payload,
					tags: allTags,
					attributes: {
						...defaultAttributes,
						...payload.attributes,
					},
				});

				const aiMateContextAttributes = getAttributesFromContexts(event.context);
				event
					.update({
						attributes: {
							...event.payload.attributes,
							...aiMateContextAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			}
		},
		[createAnalyticsEvent, maybeAtlasOperationalTags, propertiesRef],
	);

	return {
		sendAnalyticsEvent,
	};
};
